/** @format */

import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { CustomMinimalTabs } from '../../../components/CustomMinimalTabs/CustomMinimalTabs';
import GeneralDialog from '../../../components/Dialog/GeneralDialog';
import GridContainer from '../../../components/Grid/GridContainer';
import GridItem from '../../../components/Grid/GridItem';
import { configDefault } from '../../../core/config';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import { CurrencyHelper } from '../../../helpers/CurrencyHelper';
import { FormGeneralHelper } from '../../../helpers/FormGeneral';
import { NotificationHelper } from '../../../helpers/NotificationHelper';
import { PricelistSchemaHelper } from '../../../helpers/PricelistSchemaHelper';
import { roles, UserRoleHelper } from '../../../helpers/UserRoleHelper';
import { PricelistSchemaElementFractionCollectionInterface } from '../../../interfaces/api/PricelistSchemaElementFractionCollectionInterface';
import { PricelistSchemaElementPrefixCollectionInterface } from '../../../interfaces/api/PricelistSchemaElementPrefixCollectionInterface';
import FractionInterface from '../../../interfaces/billing/FractionInterface';
import PrefixInterface from '../../../interfaces/billing/PrefixInterface';
import PricelistSchemaElementFractionInterface from '../../../interfaces/billing/PricelistSchemaElementFractionInterface';
import PricelistSchemaElementInterface from '../../../interfaces/billing/PricelistSchemaElementInterface';
import PricelistSchemaElementPrefixInterface from '../../../interfaces/billing/PricelistSchemaElementPrefixInterface';
import RateSchemaInterface from '../../../interfaces/billing/RateSchemaInterface';
import { pricelistSchemaElementConstants, pricelistSchemaElementFractionConstants } from '../../../modules/pricelists/reducers';
import { rateSchemasConstants } from '../../../modules/rates/reducers';
import PricelistSchemaElementDetails from './PricelistSchemaElementDialog/PricelistSchemaElementDetails';
import PricelistSchemaElementFraction from './PricelistSchemaElementDialog/PricelistSchemaElementFraction';
import PricelistSchemaElementPrefixes from './PricelistSchemaElementDialog/PricelistSchemaElementPrefixes';
import { PricelistSchemaElementRate } from './PricelistSchemaElementDialog/PricelistSchemaElementRate';
import { PriceThresholdEntityInterface } from './PriceThresholds/PriceThresholdContainer';

const _ = require('underscore');

interface PricelistSchemaElementEditProps {
    pricelistSchemaElement?: PricelistSchemaElementInterface;
    editMode?: boolean;
    pricelistSchemaElementDialogOpen: boolean;
    togglePricelistElementDialog: () => void;
    loadPricelistSchemaElementList: () => void;
    setActivePricelistSchemaElement: React.Dispatch<React.SetStateAction<number | null>>;
    activePricelistSchemaElement: number | null;
    setPricelistSchemaElementDialogOpen: (value: boolean) => void;
}

const PricelistSchemaElementInitialState: PricelistSchemaElementInterface = {
    mainname: '',
    vat: '23',
    currencyId: null,
    type: 0,
    premium: true,
    strictPrefixMatch: false,
};

const RateSchemaInitialState: RateSchemaInterface = {
    hourRange: 24,
    countingUnit: 1,
    price1: '0.0000000000',
    initial: '0.0000000000',
    interval1: 1,
    interval2: 1,
    price2: '0.0000000000',
    duration1: 1,
    type: 0,
    days: null,
    useRatio: false,
    byDiscount: false,
    subtype: null,
};

const RateSchemaStatusInitial: any = {
    hourRange: null,
    countingUnit: null,
    price1: null,
    initial: null,
    interval1: null,
    interval2: null,
    price2: null,
    duration1: null,
    type: null,
    days: null,
    useRatio: null,
    byDiscount: null,
};

export const PricelistSchemaElementEdit = (props: PricelistSchemaElementEditProps): React.ReactElement => {
    const dispatch = useDispatch();

    const PRICELIST_URI = '/pricelist_schema_elements';
    const RATE_SCHEMA_URI = '/rate_schemas';
    const PRICELIST_SCHEMA_ELEMENT_FRACTION_URI = '/pricelist_schema_element_fractions';
    const PRICELIST_SCHEMA_ELEMENT_PREFIX_URI = '/pricelist_schema_element_prefixes';

    const [entityStatus, setEntityStatus] = useState<any>({
        mainname: null,
        activated: null,
        activatedAt: null,
    });

    const [entity, setEntity] = useState<PricelistSchemaElementInterface>(
        props.pricelistSchemaElement
            ? {
                  mainname: props.pricelistSchemaElement.mainname,
                  vat: props.pricelistSchemaElement.vat,
                  currencyId: props.pricelistSchemaElement.currencyId,
                  premium: props.pricelistSchemaElement.premium,
                  strictPrefixMatch: props.pricelistSchemaElement.strictPrefixMatch,
                  type: props.pricelistSchemaElement.type,
              }
            : PricelistSchemaElementInitialState
    );

    const [rateEntity, setRateEntity] = useState<RateSchemaInterface>(RateSchemaInitialState);
    const [rateEntityOld, setRateEntityOld] = useState<RateSchemaInterface | null>(null);

    const [rateEntityStatus, setRateEntityStatus] = useState<any>(RateSchemaStatusInitial);

    const [selectedFractions, setSelectedFractions] = useState<Array<FractionInterface>>([]);
    const [assignedFractions, setAssignedFractions] = useState<Array<FractionInterface>>([]);
    const [selectedPrefixes, setSelectedPrefixes] = useState<Array<PrefixInterface>>([]);
    const [assignedPrefixes, setAssignedPrefixes] = useState<Array<PrefixInterface>>([]);
    const [priceThresholds, setPriceThresholds] = useState<Array<PriceThresholdEntityInterface>>([]);

    const [premiumPricelist, setPremiumPricelist] = useState<boolean>(false);

    const changeInput = (e: any, stateName: string, type: string, require: boolean, maxValue: number, minValue: number): void => {
        // TODO: Add validation (use entityStatus)
        setEntity({ ...entity, [stateName]: e.target.value });
    };

    const changeSelect = (e: any, stateName: string, require: boolean): void => {
        // TODO: Add validation (use entityStatus)
        if (stateName === 'type') {
            if (e.target.value === 1) {
                setRateEntity({ ...rateEntity, type: 3 });
            } else {
                setRateEntity({ ...rateEntity, type: 0 });
            }
        }
        setEntity({ ...entity, [stateName]: e.target.value });
    };

    const changeRateInput = (e: any, stateName: string): void => {
        if (!stateName || stateName === undefined) {
            stateName = e.target.name;
        }
        setRateEntity({
            ...rateEntity,
            [stateName]: e.target.value,
        });
    };
    const changeRateSelect = (e: any, stateName: string): void => {
        setRateEntity({
            ...rateEntity,
            [stateName]: e.target.value,
        });
    };
    const setFractions = (fractions: Array<FractionInterface>): void => {
        setSelectedFractions(fractions);
    };

    const togglePremiumPricelistCheckbox = (event: any): void => {
        setEntity({ ...entity, premium: event.target.checked });
        setSelectedFractions(assignedFractions);
        setSelectedPrefixes(assignedPrefixes);
    };

    const toggleStrictPrefixMatch = (event: any): void => {
        setEntity({ ...entity, strictPrefixMatch: event.target.checked });
    };

    const loadRateSchemaForPricelistId = useCallback(
        (rateId: number) => {
            const itemLoaders = new ItemLoaders(dispatch);
            itemLoaders.getWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath(`${RATE_SCHEMA_URI}/${rateId}`, configDefault.PLATFORM_API_PREFIX)),
                rateSchemasConstants.ITEM,
                (data: RateSchemaInterface) => {
                    const beutifyEntity = beautifyCurrency(data);
                    setRateEntity(beutifyEntity);
                    setRateEntityOld(beutifyEntity);
                },
                {
                    propagateDataToCallback: true,
                    dispatchAfterCallback: false,
                }
            );
        },
        [dispatch]
    );

    const loadPricelistSchemaElement = useCallback(
        (pricelistSchemaElementId: number) => {
            const itemLoaders = new ItemLoaders(dispatch);
            itemLoaders.getWithCallback(
                FormGeneralHelper.getByUri(
                    FormGeneralHelper.getPath(`${PRICELIST_URI}/${pricelistSchemaElementId}`, configDefault.PLATFORM_API_PREFIX)
                ),
                pricelistSchemaElementConstants.ITEM,
                (data: PricelistSchemaElementInterface) => {
                    setEntity(data);
                    const rateSchemaCollection = data.rateSchemaCollection;
                    if (rateSchemaCollection) {
                        const ratesParsed = JSON.parse(rateSchemaCollection);
                        const rateId = ratesParsed[0];
                        loadRateSchemaForPricelistId(rateId);
                    } else {
                        setRateEntity(RateSchemaInitialState);
                    }
                },
                {
                    propagateDataToCallback: true,
                    dispatchAfterCallback: false,
                }
            );
        },
        [dispatch, loadRateSchemaForPricelistId]
    );

    const loadFractionsForPricelistId = useCallback(
        (picelistSchemaId: number) => {
            const itemLoaders = new ItemLoaders(dispatch);
            itemLoaders.getListWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath(PRICELIST_SCHEMA_ELEMENT_FRACTION_URI, configDefault.PLATFORM_API_PREFIX)),
                pricelistSchemaElementFractionConstants.LIST,
                [{ name: 'pricelistSchemaElementId', value: picelistSchemaId }],
                [],
                (data: PricelistSchemaElementFractionCollectionInterface) => {
                    if (data['hydra:member']) {
                        const fractions: Array<FractionInterface> = [];
                        data['hydra:member'].forEach((el: PricelistSchemaElementFractionInterface) => {
                            if (el.fraction) {
                                fractions.push(el.fraction);
                            }
                        });
                        if (fractions && fractions.length > 0) {
                            setSelectedFractions(fractions);
                            setAssignedFractions(fractions);
                        }
                    }
                },
                {
                    propagateDataToCallback: true,
                    dispatchAfterCallback: false,
                }
            );
        },
        [dispatch]
    );

    const loadPrefixesForPricelistId = useCallback(
        (picelistSchemaId: number) => {
            const itemLoaders = new ItemLoaders(dispatch);
            itemLoaders.getListWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath(PRICELIST_SCHEMA_ELEMENT_PREFIX_URI, configDefault.PLATFORM_API_PREFIX)),
                pricelistSchemaElementFractionConstants.LIST,
                [
                    { name: 'pricelistSchemaElementId', value: picelistSchemaId },
                    { name: 'itemsPerPage', value: 999999 },
                ],
                [],
                (data: PricelistSchemaElementPrefixCollectionInterface) => {
                    if (data['hydra:member']) {
                        const prefixes: Array<PrefixInterface> = [];
                        data['hydra:member'].forEach((el: PricelistSchemaElementPrefixInterface) => {
                            if (el.prefix) {
                                prefixes.push(el.prefix);
                            }
                        });
                        if (prefixes && prefixes.length > 0) {
                            setSelectedPrefixes(prefixes);
                            setAssignedPrefixes(prefixes);
                        }
                    }
                },
                {
                    propagateDataToCallback: true,
                    dispatchAfterCallback: false,
                }
            );
        },
        [dispatch]
    );

    const savePricelistSchema = (): void => {
        let params: any = {};
        if (entity.mainname.trim().length === 0) {
            NotificationHelper.notifyError('pricelists:form.pricelist_element_short_name', dispatch);
            return;
        }

        fixComma(rateEntity);
        // TODO: Temporary here becouse we using only one rate. In future we may create multiple rates so move assign to array to add next rate button
        const ratesArr = [rateEntity];
        if (ratesArr) {
            if (!_.isEqual(rateEntity, rateEntityOld)) {
                params[PricelistSchemaHelper.PRICELIST_SCHEMA_ELEMENT_CONSTANTS.RATE_SCHEMA] = ratesArr;
            }
        }

        if (selectedFractions.length > 0 && !entity.premium) {
            const fractionIds = selectedFractions.map((el: FractionInterface) => {
                return el.id;
            });
            params[PricelistSchemaHelper.PRICELIST_SCHEMA_ELEMENT_CONSTANTS.PRICELIST_SCHEMA_ELEMENT_FRACTION] = fractionIds;
        }
        if (selectedPrefixes.length > 0 && entity.premium) {
            const prefixIds = selectedPrefixes.map((el: PrefixInterface) => {
                return el.id;
            });
            params[PricelistSchemaHelper.PRICELIST_SCHEMA_ELEMENT_CONSTANTS.PRICELIST_SCHEMA_ELEMENT_PREFIX] = prefixIds;
        }

        if (priceThresholds.length > 0) {
            params[PricelistSchemaHelper.PRICELIST_SCHEMA_ELEMENT_CONSTANTS.PRICE_THRESHOLD] = priceThresholds;
        }

        entity.params = JSON.stringify(params);

        const itemLoaders = new ItemLoaders(dispatch);
        if (props.activePricelistSchemaElement) {
            itemLoaders.putWithCallback(
                FormGeneralHelper.getByUri(
                    FormGeneralHelper.getPath(`${PRICELIST_URI}/${props.activePricelistSchemaElement}`, configDefault.PLATFORM_API_PREFIX)
                ),
                pricelistSchemaElementConstants.EDIT,
                entity,
                (data: PricelistSchemaElementInterface) => {
                    props.loadPricelistSchemaElementList();
                    setEntity(PricelistSchemaElementInitialState);
                    setRateEntity(RateSchemaInitialState);
                    setRateEntityOld(null);
                    setRateEntityStatus(RateSchemaStatusInitial);
                    setPriceThresholds([]);
                    props.setPricelistSchemaElementDialogOpen(false);
                    props.setActivePricelistSchemaElement(null);
                },
                {
                    dispatchAfterCallback: true,
                    propagateDataToCallback: false,
                }
            );
        } else {
            itemLoaders.postWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath(PRICELIST_URI, configDefault.PLATFORM_API_PREFIX)),
                pricelistSchemaElementConstants.ADD,
                entity,
                (): void => {
                    props.loadPricelistSchemaElementList();
                    setEntity(PricelistSchemaElementInitialState);
                    setRateEntity(RateSchemaInitialState);
                    setRateEntityOld(null);
                    setRateEntityStatus(RateSchemaStatusInitial);
                    setPriceThresholds([]);
                    props.setPricelistSchemaElementDialogOpen(false);
                },
                {
                    propagateDataToCallback: true,
                    dispatchAfterCallback: true,
                }
            );
        }
    };

    // TODO: find better solution for this
    const beautifyCurrency = (rate: RateSchemaInterface): RateSchemaInterface => {
        if (rate.price1) {
            rate.price1 = CurrencyHelper.numericToStandardPln(rate.price1, 10, true, '.');
        }
        if (rate.price2) {
            rate.price2 = CurrencyHelper.numericToStandardPln(rate.price2, 10, true, '.');
        }
        if (rate.initial) {
            rate.initial = CurrencyHelper.numericToStandardPln(rate.initial, 10, true, '.');
        }

        return rate;
    };

    const fixComma = (rate: RateSchemaInterface): void => {
        let fixedRate = rate;
        if (rate.price1) {
            const value = rate.price1.replace(/,/g, '.');
            fixedRate.price1 = value;
        }
        if (rate.price2) {
            const value = rate.price2.replace(/,/g, '.');
            fixedRate.price2 = value;
        }
        if (rate.initial) {
            const value = rate.initial.replace(/,/g, '.');
            fixedRate.initial = value;
        }

        setRateEntity(fixedRate);
    };

    const useRationCallback = (checked: boolean): void => {
        const newRate: RateSchemaInterface = { ...rateEntity, useRatio: checked };
        setRateEntity(newRate);
    };

    useEffect(() => {
        if (props.activePricelistSchemaElement) {
            loadPricelistSchemaElement(props.activePricelistSchemaElement);
            loadPrefixesForPricelistId(props.activePricelistSchemaElement);
            loadFractionsForPricelistId(props.activePricelistSchemaElement);
        }
    }, [
        props.activePricelistSchemaElement,
        loadPricelistSchemaElement,
        loadFractionsForPricelistId,
        loadPrefixesForPricelistId,
        props.pricelistSchemaElement,
    ]);

    useEffect(() => {
        if (!props.pricelistSchemaElementDialogOpen) {
            setSelectedFractions([]);
            setSelectedPrefixes([]);
            setEntity(PricelistSchemaElementInitialState);
            setRateEntity(RateSchemaInitialState);
        }
    }, [props.pricelistSchemaElementDialogOpen]);

    // useEffect(() => {
    //     console.log(rateEntity);
    // }, [rateEntity]);

    return (
        <GeneralDialog
            maxWidth={'lg'}
            open={props.pricelistSchemaElementDialogOpen}
            dialogType={'action'}
            fullWidth
            title={'pricelists:t.add_pricelist_element_form'}
            disagreeCallback={props.togglePricelistElementDialog}
            disagreeCallbackTextLabel={'general:common.cancel'}
            agreeCallback={
                UserRoleHelper.checkRole(
                    props.activePricelistSchemaElement
                        ? roles.ROLE_PLATFORM_PRICELIST_SCHEMA_ELEMENT_PUT
                        : roles.ROLE_PLATFORM_PRICELIST_SCHEMA_ELEMENT_POST
                )
                    ? savePricelistSchema
                    : (): void => {}
            }
            agreeCallbackTextLabel={'general:common.save'}
        >
            <GridContainer style={{ height: 500 }} spacing={1}>
                <GridItem xs={12} md={12} lg={12}>
                    <CustomMinimalTabs
                        resetActiveCard={props.pricelistSchemaElementDialogOpen}
                        tabs={[
                            {
                                name: 'pricelists:b.pricelist_element_details',
                                element: (
                                    <PricelistSchemaElementDetails
                                        entityStatus={entityStatus}
                                        entity={entity}
                                        changeInput={changeInput}
                                        changeSelect={changeSelect}
                                        changeRateSelect={changeRateSelect}
                                        setRateEntity={setRateEntity}
                                        rateEntity={rateEntity}
                                        togglePremiumPricelistCheckbox={togglePremiumPricelistCheckbox}
                                        toggleStrictPrefixMatch={toggleStrictPrefixMatch}
                                        premiumPricelist={premiumPricelist}
                                    />
                                ),
                            },
                            {
                                name: 'pricelists:b.pricelist_element_rate',
                                element: (
                                    <PricelistSchemaElementRate
                                        rateEntityStatus={rateEntityStatus}
                                        rateEntity={rateEntity}
                                        changeRateSelect={changeRateSelect}
                                        changeRateInput={changeRateInput}
                                        entity={entity}
                                        priceThresholds={priceThresholds}
                                        setPriceThresholds={setPriceThresholds}
                                        useRationCallback={useRationCallback}
                                        priceThresholdsOpen={props.pricelistSchemaElementDialogOpen}
                                    />
                                ),
                            },
                            {
                                name: entity.premium ? 'pricelists:b.pricelist_element_prefixes' : 'pricelists:b.pricelist_element_fraction',
                                element: entity.premium ? (
                                    <PricelistSchemaElementPrefixes
                                        setPrefixes={setSelectedPrefixes}
                                        selectedPrefixes={selectedPrefixes}
                                        entity={entity}
                                    />
                                ) : (
                                    <PricelistSchemaElementFraction
                                        setFractions={setFractions}
                                        selectedFractions={selectedFractions}
                                        entity={entity}
                                    />
                                ),
                            },
                        ]}
                    />
                </GridItem>
            </GridContainer>
        </GeneralDialog>
    );
};
